import { useEffect, useRef, useState } from "react";
import { getInitials, V2_ZONE_META } from "./v2OverviewUtils";
import { loadPlayerPhoto, peekPlayerPhoto } from "./v2PlayerPhotos";

export default function V2PlayerAvatar({
  name,
  color,
  zone,
  size = 28,
  clubId,
  playerId,
}) {
  const ring = V2_ZONE_META[zone]?.color || "#625ea6";
  const nodeRef = useRef(null);
  const cached = peekPlayerPhoto(playerId);
  const [visible, setVisible] = useState(false);
  const [src, setSrc] = useState(() => (typeof cached === "string" ? cached : null));
  const [loading, setLoading] = useState(() => Boolean(clubId && playerId && cached === undefined));

  useEffect(() => {
    if (!clubId || !playerId) return undefined;
    const node = nodeRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "160px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [clubId, playerId]);

  useEffect(() => {
    if (!visible || !clubId || !playerId) return undefined;
    const known = peekPlayerPhoto(playerId);
    if (known !== undefined) {
      setSrc(typeof known === "string" ? known : null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    loadPlayerPhoto(clubId, playerId)
      .then((url) => {
        if (cancelled) return;
        setSrc(url);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setSrc(null);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clubId, playerId, visible]);

  return (
    <span
      ref={nodeRef}
      className="relative grid overflow-hidden rounded-full font-bold text-[#f6f3ff] place-items-center"
      style={{
        width: size,
        height: size,
        background: color || "#2B3A8F",
        boxShadow: `0 0 0 2px #110c3d, 0 0 0 3px ${ring}`,
        fontSize: size < 30 ? 8 : 11,
      }}
      aria-hidden="true"
    >
      {src ? (
        <img src={src} alt="" className="h-full w-full rounded-full object-cover" />
      ) : (
        getInitials(name)
      )}
      {loading && !src ? (
        <span className="absolute inset-0 animate-pulse rounded-full bg-[rgba(255,255,255,0.12)]" />
      ) : null}
    </span>
  );
}
